/// <amd-module name="Test/TestPlansHubRefresh/Pivots/ColumnOptions/ColumnOptionsCommonUtils" />
import * as Resources from "Test/TestPlansHubRefresh/Pivots/ColumnOptions/Resources";
import { TestCase, TestPoint } from "TestPlanCommon/Contracts/TestPlan";

export interface IColumnOptions {
    name: string;
    isWorkItemField: boolean;
    authorPivot: boolean; // set to true only if this is a non default column in the author pivot
    executePivot: boolean; // set to true only if this is a non default column in the execute pivot
}

export interface IColumnOptionKeyValuePair {
    key: string;
    name: string;
}

export interface IWorkItemColumnOptions extends IColumnOptions {
    getDisplayValue(fieldValue: any): any;
}

export interface IOtherColumnOptions extends IColumnOptions {
    getDisplayValue(testCase: TestCase, testPoint?: TestPoint): any;
}

export const WorkItemColumnOptions: { [id: string]: IWorkItemColumnOptions } = {
    "System.AssignedTo": {
        name: Resources.AssignedTo,
        isWorkItemField: true,
        authorPivot: true,
        executePivot: true,
        getDisplayValue: (fieldValue: any) => {
            if (fieldValue) {
                if (fieldValue.identityRef) {
                    return fieldValue.identityRef.displayName;
                }
                if (fieldValue.distinctDisplayName) {
                    return fieldValue.distinctDisplayName;
                }
                return fieldValue;
            }
            else {
                return Resources.UnassignedIdentity;
            }
        }
    },
    "Microsoft.VSTS.TCM.AutomationStatus": {
        name: Resources.AutomationStatus,
        isWorkItemField: true,
        authorPivot: true,
        executePivot: true,
        getDisplayValue: (fieldValue: any) => {
            return fieldValue;
        }
    },
    "Microsoft.VSTS.TCM.AutomatedTestName": {
        name: Resources.AutomatedTestName,
        isWorkItemField: true,
        authorPivot: true,
        executePivot: true,
        getDisplayValue: (fieldValue: any) => {
            return fieldValue;
        }
    },
    "Microsoft.VSTS.Common.Priority": {
        name: Resources.Priority,
        isWorkItemField: true,
        authorPivot: true,
        executePivot: true,
        getDisplayValue: (fieldValue: any) => {
            return fieldValue;
        }
    },
    "Microsoft.VSTS.Build.IntegrationBuild": {
        name: Resources.IntegrationBuild,
        isWorkItemField: true,
        authorPivot: false,
        executePivot: true,
        getDisplayValue: (fieldValue: any) => {
            return fieldValue;
        }
    },
    "System.Id": {
        name: Resources.Id,
        isWorkItemField: true,
        authorPivot: true,
        executePivot: true,
        getDisplayValue: (fieldValue: any) => {
            return fieldValue;
        }
    },
    "System.AreaPath": {
        name: Resources.AreaPath,
        isWorkItemField: true,
        authorPivot: true,
        executePivot: true,
        getDisplayValue: (fieldValue: any) => {
            return fieldValue;
        }
    },
    "System.IterationPath": {
        name: Resources.IterationPath,
        isWorkItemField: true,
        authorPivot: true,
        executePivot: true,
        getDisplayValue: (fieldValue: any) => {
            return fieldValue;
        }
    },
    "System.Tags": {
        name: Resources.Tags,
        isWorkItemField: true,
        authorPivot: true,
        executePivot: true,
        getDisplayValue: (fieldValue: any) => {
            return fieldValue;
        }
    },
    "System.TeamProject": {
        name: Resources.Project,
        isWorkItemField: true,
        authorPivot: true,
        executePivot: false,
        getDisplayValue: (fieldValue: any) => {
            return fieldValue;
        }
    },
    "System.CreatedBy": {
        name: Resources.CreatedBy,
        isWorkItemField: true,
        authorPivot: true,
        executePivot: false,
        getDisplayValue: (fieldValue: any) => {
            if (fieldValue && fieldValue.identityRef) {
                return fieldValue.identityRef.displayName;
            }
            if (fieldValue && fieldValue.distinctDisplayName) {
                return fieldValue.distinctDisplayName;
            }
            return "";
        }
    },
    "System.CreatedDate": {
        name: Resources.CreatedDate,
        isWorkItemField: true,
        authorPivot: true,
        executePivot: false,
        //This is later converted to string before rendering(to allow sorting)
        getDisplayValue: (fieldValue: any) => {
            return new Date(fieldValue);
        }
    },
    "System.ChangedBy": {
        name: Resources.ChangedBy,
        isWorkItemField: true,
        authorPivot: true,
        executePivot: false,
        getDisplayValue: (fieldValue: any) => {
            if (fieldValue && fieldValue.identityRef) {
                return fieldValue.identityRef.displayName;
            }
            if (fieldValue && fieldValue.distinctDisplayName) {
                return fieldValue.distinctDisplayName;
            }
            return "";
        }
    },
    "System.ChangedDate": {
        name: Resources.ChangedDate,
        isWorkItemField: true,
        authorPivot: true,
        executePivot: false,
        //This is later converted to string before rendering(to allow sorting)
        getDisplayValue: (fieldValue: any) => {
            return new Date(fieldValue);
        }
    },
    "System.State": {
        name: Resources.State,
        isWorkItemField: true,
        authorPivot: true,
        executePivot: false,
        getDisplayValue: (fieldValue: any) => {
            return fieldValue;
        }
    }
};

export const OtherColumnOptions: { [id: string]: IOtherColumnOptions } = {
    "TCM.SuiteName": {
        name: Resources.SuiteName,
        isWorkItemField: false,
        authorPivot: true,
        executePivot: true,
        getDisplayValue: (testCase: TestCase, testPoint: TestPoint) => {
            if (testPoint) {
                return testPoint && testPoint.testSuite && testPoint.testSuite.name;
            } else {
                return testCase && testCase.testSuite && testCase.testSuite.name;
            }
        }
    },
    "TCM.Build": {
        name: Resources.Build,
        isWorkItemField: false,
        authorPivot: false,
        executePivot: true,
        getDisplayValue: (testCase: TestCase, testPoint: TestPoint) => {
            return testPoint && testPoint.results && testPoint.results.lastRunBuildNumber;
        }
    },
    "TCM.LastRunBy": {
        name: Resources.LastRunBy,
        isWorkItemField: false,
        authorPivot: false,
        executePivot: true,
        getDisplayValue: (testCase: TestCase, testPoint: TestPoint) => {
            return (
                testPoint &&
                testPoint.results &&
                testPoint.results.lastResultDetails &&
                testPoint.results.lastResultDetails.runBy &&
                testPoint.results.lastResultDetails.runBy.displayName
            );
        }
    },
    "TCM.TestPointConfigurationName": {
        name: Resources.Configuration,
        isWorkItemField: false,
        authorPivot: false,
        executePivot: true,
        getDisplayValue: (testCase: TestCase, testPoint: TestPoint) => {
            return testPoint && testPoint.configuration && testPoint.configuration.name;
        }
    },
    "TCM.TestPointTester": {
        name: Resources.Tester,
        isWorkItemField: false,
        authorPivot: false,
        executePivot: true,
        getDisplayValue: (testCase: TestCase, testPoint: TestPoint) => {
            if (testPoint && testPoint.tester) {
                let fieldValue = testPoint.tester;
                if (fieldValue && fieldValue.displayName) {
                    return fieldValue.displayName;
                } else {
                    return Resources.UnassignedIdentity;
                }
            }
        }
    },
    "TCM.LastRunDuration": {
        name: Resources.LastRunDuration,
        isWorkItemField: false,
        authorPivot: false,
        executePivot: true,
        getDisplayValue: (testCase: TestCase, testPoint: TestPoint) => {
            if (testPoint && testPoint.results && testPoint.results.lastResultDetails && testPoint.results.lastResultDetails.duration) {
                return testPoint.results.lastResultDetails.duration;
            }
            return 0;
        }
    }
};

export const CustomFieldColumn: IWorkItemColumnOptions = {
    name: Resources.CustomField,
    isWorkItemField: true,
    authorPivot: true,
    executePivot: true,
    getDisplayValue: (fieldValue: any) => {
        if (fieldValue && fieldValue.identityRef) {
            return fieldValue.identityRef.displayName;
        }
        if (fieldValue && fieldValue.distinctDisplayName) {
            return fieldValue.distinctDisplayName;
        }
        if (fieldValue && fieldValue.distinctDisplayName) {
            return fieldValue.distinctDisplayName;
        }
        if (typeof fieldValue !== "number") {
            // boolean and date type columns require them to be converted to string for rendering otherwise displayed empty
            return String(fieldValue);
        }
        return fieldValue;
    }
};

export function getCustomFieldName(columnOptionField: string, customColumnFieldMap?: { [columnId: string]: string }) {
    let name;
    if (customColumnFieldMap) {
        name = customColumnFieldMap[columnOptionField];
    }
    else {
        name = columnOptionField.substring(columnOptionField.lastIndexOf(".") + 1);
    }
    return {
        id: columnOptionField,
        name: name
    };
}

export function isCoreField(field: string): boolean {
    if (field.toLowerCase().startsWith("system.")) {
        return true;
    }
    return false;
}

export function isoobfield(field: string): boolean {
    if (field.toLowerCase().startsWith("microsoft.vsts.")) {
        return true;
    }
    return false;
}

//since old custom columns do not start with custom.
export function isCustomField(field: string): boolean {
    if (!isCoreField(field) && !isoobfield(field) && !OtherColumnOptions[field]) {
        return true;
    }
    return false;
}
